import {
  Project,
  Button,
  BaseDialog,
  Input,
  useToast,
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  useFSTrackEvent,
  ProjectNew,
} from '@/dc-shared-web/src'
import { useUpdateProject } from '@/dc-shared-web/src/hooks/api/project/useUpdateProject'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { FS } from '@/dc-shared-web/src/lib/trackingConstants'

export const RenameProjectDialog = ({
  project,
  onClose,
  onSuccess,
}: {
  project: Project | ProjectNew
  onClose: () => void
  onSuccess: () => void
}) => {
  const { trackEvent } = useFSTrackEvent()
  const formSchema = getFormSchema(String(project.name))
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: project.name,
    },
  })
  const { toast } = useToast()
  const { mutate: updateProject, isLoading } = useUpdateProject({
    projectUUID: project.uuid,
  })

  const onSave = (data: z.infer<typeof formSchema>) => {
    updateProject(
      {
        field: 'name',
        value: data.name,
        id: project.uuid,
      },
      {
        onSuccess: () => {
          trackEvent(FS.PROJECT_RENAMED)

          toast({
            variant: 'success',
            title: 'Project renamed',
            description: `"${project.name}" renamed to "${data.name}".`,
          })

          onSuccess()
        },
      }
    )
  }

  const onDialogClose = () => {
    /* We avoid closing the dialog if an update is still in progress */
    if (isLoading) return

    onClose()
  }

  return (
    <BaseDialog
      title='Rename project'
      isOpen
      onClose={onDialogClose}
      primaryActionSlot={
        <Button
          onClick={form.handleSubmit(onSave)}
          disabled={isLoading}
          data-testid='rename-project-dashboard-trigger'
        >
          {isLoading ? 'Renaming...' : 'Rename'}
        </Button>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSave)}>
          <FormField
            control={form.control}
            name='name'
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    placeholder='Enter project name'
                    autoFocus
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </form>
      </Form>
    </BaseDialog>
  )
}

const getFormSchema = (currentProjectName: string) =>
  z.object({
    name: z.coerce
      .string()
      .min(1, 'Project name cannot be blank')
      .refine((value) => value !== currentProjectName, {
        message: `New name must be different from current name: "${currentProjectName}"`,
      }),
  })
